import type { Merge } from 'type-fest'
import type { FileGroupType, JobFile, Reaction } from '~/@types/backend-types'
import { cachedInArray, groupArray, tranformIntoCachedInArray } from '~/utilities/arrayUtils'
import { Day_asString, getFileDay } from '~/utilities/dateOperations'
import { Dictionary_filter, Dictionary_forEach, Dictionary_map } from '~/utilities/dictionaryUtils'

import type { Action } from '../common/actions'
import type { FileDescription } from '../job/actions'
import {
    FileRangeWasFetched,
    FileWasCopiedToJob,
    FileWasRemovedFromJob,
    FilesDeletionStarted,
    FilesRestorationSucceeded,
    FilesWereAddedToJob,
} from '../job/actions'
import { TimelineSectionReference__allDays } from '../timeline'
import { SectionsWereExpired } from '../timeline/actions'

import {
    FileCommentAborted,
    FileCommentError,
    FileCommentSubmitSuccessful,
    FileCommentSubmitted,
    FileDeletionFailed,
    FileDeletionStarted,
    FileDimensionsDiscovered,
    FileObjectsWereFetched,
    JobFilesWereExpired,
    ReactionChangesSubmitError,
    ReactionChangesSubmitSuccessfull,
    ReactionChangesSubmitted,
    VideoTranscodeError,
    VideoTranscodeQueued,
    VideoTranscodeReady,
    VideoTranscodeStarted,
} from './actions'

export enum CommentStatus {
    NOT_YET_SUBMITTED = 'NOT_YET_SUBMITTED', // Default state
    PENDING = 'PENDING', // Comment is sent to backend, waiting for response
    ERROR = 'ERROR', // Something went wrong when trying to save comment
}

export enum EncodingStatus {
    FETCHING = 'FETCHING', // Waiting for encoding status to be loaded
    PROCESSING = 'PROCESSING', // In Queue encoding state
    ENCODED = 'ENCODED', // Encoded
    ERROR = 'ERROR', // Encoding check failed
}

export type ExtendedJobFile = Merge<
    Omit<JobFile, 'aspect_ratio' | 'file_type' | 'group_id' | 'group_type' | 'id' | 'revisions'>,
    {
        jobID: JobID
        fileID: FileID
        addedBy: UserID
        encodingStatus?: EncodingStatus
        pendingComment: string
        pendingCommentStatus: CommentStatus
        isPendingServerDelete: boolean
        pendingReaction?: Reaction
        isDeleted: boolean
        group?: {
            type: FileGroupType
            id: string
            isMaster: boolean
        }
    }
>

export type FilesState = {
    files: Record<JobID, Record<FileID, ExtendedJobFile>> // Files grouped by job
    _fileJobIDMap: Record<FileID, JobID> // Internal tracking: most actions are FileID-specific - TODO: Make obsolete
}

const initialState: FilesState = { files: {}, _fileJobIDMap: {} }

const hasFile = (state: FilesState, fileID: FileID, jobID?: JobID): boolean => {
    jobID = jobID || state._fileJobIDMap[fileID]
    return (
        jobID !== undefined &&
        state.files[jobID] !== undefined &&
        state.files[jobID][fileID] !== undefined
    )
}

function newStateWithElementChanges(
    state: FilesState,
    elementId: FileID,
    changes: Partial<ExtendedJobFile>,
): FilesState {
    if (!hasFile(state, elementId)) {
        return state
    }
    const jobID = state._fileJobIDMap[elementId]
    const element: ExtendedJobFile = {
        ...state.files[jobID][elementId],
        ...changes,
    }
    return {
        ...state,
        files: {
            ...state.files,
            [jobID]: {
                ...state.files[jobID],
                [elementId]: element,
            },
        },
    }
}

type FileChanges = Record<JobID, Record<FileID, Partial<ExtendedJobFile>>> // Changes keyed by jobID/fileID
const newStateWithMultipleChanges = (state: FilesState, changes: FileChanges): FilesState => {
    const files = Dictionary_map(state.files, (jobFiles, jobID) => {
        if (changes[jobID] === undefined) {
            return jobFiles
        }
        return Dictionary_map(jobFiles, (file: ExtendedJobFile, fileID) => {
            if (changes[jobID][fileID] === undefined) {
                return file
            }
            return { ...file, ...changes[jobID][fileID] }
        })
    })
    return { ...state, files }
}

const groupByJob = <T>(
    items: DictionaryOf<T>,
    groups: DictionaryOf<JobID>,
): Record<JobID, DictionaryOf<T>> => {
    const grouped: Record<JobID, DictionaryOf<T>> = {}
    Dictionary_forEach(items, (item, key) => {
        grouped[groups[key]] = grouped[groups[key]] || {}
        grouped[groups[key]][key] = item as T
    })
    return grouped
}

export const fileFromFileDescription = ({
    user_uuid,
    group_type,
    group_id,
    master,
    ...fileInfo
}: FileDescription): ExtendedJobFile => {
    const group =
        group_type !== undefined && group_id !== undefined
            ? {
                  type: group_type,
                  id: group_id,
                  isMaster: master === '1',
              }
            : undefined
    return {
        ...fileInfo,
        addedBy: user_uuid,
        pendingComment: '',
        pendingCommentStatus: CommentStatus.NOT_YET_SUBMITTED,
        isPendingServerDelete: false,
        isDeleted: false,
        group,
    }
}

// TODO: separate this list to single state
type FilePathList = Record<JobID, DictionaryOf<FileID>> // indexed by [jobID][path]
const filePathListFromFiles = ({ files }: FilesState): FilePathList =>
    Dictionary_map(files, (jobFiles) => {
        const byPath: DictionaryOf<FileID> = {}
        Dictionary_forEach(jobFiles, (file: ExtendedJobFile) => {
            byPath[file.path] = file.fileID
        })
        return byPath
    })

const stateWithNewFiles = (originState: FilesState, newFiles: ExtendedJobFile[]): FilesState => {
    const originList = filePathListFromFiles(originState)
    const filesByJob = groupArray(newFiles, (f) => f.jobID)

    const files = { ...originState.files }
    const jobMap = { ...originState._fileJobIDMap }

    Dictionary_forEach(filesByJob, (jobFiles: ExtendedJobFile[], jobID) => {
        const filesByPath = originList[jobID] || {}
        const newJobFiles = { ...originState.files[jobID] }
        jobFiles.forEach((f) => {
            if (filesByPath[f.path] && filesByPath[f.path] !== f.fileID) {
                delete newJobFiles[filesByPath[f.path]]
            }
            newJobFiles[f.fileID] = { ...newJobFiles[f.fileID], ...f }
            jobMap[f.fileID] = f.jobID
        })
        files[jobID] = newJobFiles
    })

    return { ...originState, files, _fileJobIDMap: jobMap }
}

export function filesReducer(state: FilesState = initialState, action: Action): FilesState {
    if (FilesWereAddedToJob.match(action)) {
        return stateWithNewFiles(state, action.payload.files.map(fileFromFileDescription))
    }
    if (FileWasCopiedToJob.match(action)) {
        const { to, from } = action.payload // TODO: make `from` be a FileReference
        if (hasFile(state, to.fileID, to.jobID) || !hasFile(state, from)) {
            return state
        }
        const newFile: ExtendedJobFile = {
            ...state.files[state._fileJobIDMap[from]][from],
            fileID: action.payload.to.fileID,
            jobID: action.payload.to.jobID,
        }
        return stateWithNewFiles(state, [newFile])
    }

    if (FileRangeWasFetched.match(action)) {
        return stateWithNewFiles(state, action.payload.files.map(fileFromFileDescription))
    }

    if (FileObjectsWereFetched.match(action)) {
        return stateWithNewFiles(state, action.payload.map(fileFromFileDescription))
    }

    if (FileWasRemovedFromJob.match(action)) {
        return newStateWithElementChanges(state, action.payload.fileID, {
            isDeleted: true,
            isPendingServerDelete: false,
        })
    }

    if (FilesRestorationSucceeded.match(action)) {
        const changes: DictionaryOf<{
            isDeleted: boolean
            isPendingServerDelete: boolean
        }> = {}
        action.payload.files.forEach((fileID) => {
            changes[fileID] = { isDeleted: false, isPendingServerDelete: false }
        })

        return newStateWithMultipleChanges(state, groupByJob(changes, state._fileJobIDMap))
    }

    if (FilesDeletionStarted.match(action)) {
        const changes: DictionaryOf<{ isPendingServerDelete: boolean }> = {}
        action.payload.files.forEach((fileID) => {
            changes[fileID] = { isPendingServerDelete: false }
        })

        return newStateWithMultipleChanges(state, groupByJob(changes, state._fileJobIDMap))
    }

    if (FileDeletionStarted.match(action)) {
        if (hasFile(state, action.payload)) {
            return newStateWithElementChanges(state, action.payload, {
                isPendingServerDelete: true,
            })
        }
    }

    if (FileDeletionFailed.match(action)) {
        if (hasFile(state, action.payload)) {
            return newStateWithElementChanges(state, action.payload, {
                isPendingServerDelete: false,
            })
        }
    }

    if (FileCommentSubmitted.match(action)) {
        return newStateWithElementChanges(state, action.payload.fileID, {
            pendingCommentStatus: CommentStatus.PENDING,
            pendingComment: action.payload.comment,
        })
    }
    if (FileCommentSubmitSuccessful.match(action)) {
        return newStateWithElementChanges(state, action.payload, {
            pendingComment: '',
            pendingCommentStatus: CommentStatus.NOT_YET_SUBMITTED,
        })
    }
    if (FileCommentError.match(action)) {
        return newStateWithElementChanges(state, action.payload, {
            pendingCommentStatus: CommentStatus.ERROR,
        })
    }
    if (FileCommentAborted.match(action)) {
        return newStateWithElementChanges(state, action.payload, {
            pendingCommentStatus: CommentStatus.NOT_YET_SUBMITTED,
        })
    }

    if (VideoTranscodeStarted.match(action)) {
        return newStateWithElementChanges(state, action.payload, {
            encodingStatus: EncodingStatus.FETCHING,
        })
    }
    if (VideoTranscodeQueued.match(action)) {
        return newStateWithElementChanges(state, action.payload, {
            encodingStatus: EncodingStatus.PROCESSING,
        })
    }
    if (VideoTranscodeReady.match(action)) {
        return newStateWithElementChanges(state, action.payload, {
            encodingStatus: EncodingStatus.ENCODED,
        })
    }
    if (VideoTranscodeError.match(action)) {
        return newStateWithElementChanges(state, action.payload, {
            encodingStatus: EncodingStatus.ERROR,
        })
    }

    if (FileDimensionsDiscovered.match(action)) {
        const changes: Record<JobID, DictionaryOf<{ width: number; height: number }>> = {}
        action.payload.forEach(({ fileID, width, height }) => {
            const jobID = state._fileJobIDMap[fileID]
            changes[jobID] = changes[jobID] || {}
            changes[jobID][fileID] = { width, height }
        })
        return newStateWithMultipleChanges(state, changes)
    }

    if (ReactionChangesSubmitted.match(action)) {
        return newStateWithElementChanges(state, action.payload.fileID, {
            pendingReaction: action.payload.reaction,
        })
    }

    if (ReactionChangesSubmitSuccessfull.match(action)) {
        return newStateWithElementChanges(state, action.payload, {
            pendingReaction: undefined,
        })
    }

    if (ReactionChangesSubmitError.match(action)) {
        return newStateWithElementChanges(state, action.payload, {
            pendingReaction: undefined,
        })
    }
    if (JobFilesWereExpired.match(action)) {
        const isExpiredJob = cachedInArray(action.payload)
        return {
            ...state,
            files: Dictionary_filter(state.files, (_, jobID) => !isExpiredJob(jobID)),
        }
    }

    if (SectionsWereExpired.match(action)) {
        const expiredDays = action.payload.expiredSections.flatMap(
            TimelineSectionReference__allDays,
        )
        const isExpiredDay = tranformIntoCachedInArray(expiredDays, Day_asString)
        return {
            ...state,
            files: Dictionary_map(state.files, (jobFiles, jobID) => {
                if (jobID === action.payload.jobID) {
                    return Dictionary_filter(
                        jobFiles,
                        (file: ExtendedJobFile) => !isExpiredDay(getFileDay(file)),
                    )
                }
                return jobFiles
            }),
        }
    }

    return state
}

export const filesReducerMapObj = {
    files: filesReducer,
}

export type StateWithFiles = StateOfReducerMapObj<typeof filesReducerMapObj>
